'use client';

import { useEffect, useState } from 'react';
import { Flame, PenLine } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import DashboardMetricCard from './DashboardMetricCard';

type Streak = { current: number; longest: number };
type StreaksResponse = { login: Streak; posting: Streak };

export default function StreaksCard() {
  const [data, setData] = useState<StreaksResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/proxy/api/profile/me/streaks', undefined, { method: 'GET' });
        if (!res.ok) { if (!cancelled) setError(true); return; }
        const json = (await res.json()) as StreaksResponse;
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Suppress the card entirely if both streaks are zero — design system rule:
  // no "0-day streak" engagement-bait copy.
  if (error || data === null) return null;
  if ((data.login?.current ?? 0) === 0 && (data.posting?.current ?? 0) === 0) return null;

  const flame = <Flame className="h-4 w-4" strokeWidth={1.75} />;
  return (
    <DashboardMetricCard title="Streaks" icon={flame}>
      <ul className="space-y-3">
        {data.login.current > 0 ? (
          <li className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Flame className="h-5 w-5 text-amber-500 shrink-0" strokeWidth={1.75} />
              <div className="min-w-0">
                <div className="text-sm font-semibold tabular-nums">
                  {data.login.current}-day login streak
                </div>
                <div className="text-xs text-muted-foreground">
                  Best: {data.login.longest} day{data.login.longest === 1 ? '' : 's'}
                </div>
              </div>
            </div>
          </li>
        ) : null}
        {data.posting.current > 0 ? (
          <li className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <PenLine className="h-5 w-5 text-primary shrink-0" strokeWidth={1.75} />
              <div className="min-w-0">
                <div className="text-sm font-semibold tabular-nums">
                  {data.posting.current}-day posting streak
                </div>
                <div className="text-xs text-muted-foreground">
                  Best: {data.posting.longest} day{data.posting.longest === 1 ? '' : 's'}
                </div>
              </div>
            </div>
          </li>
        ) : null}
      </ul>
    </DashboardMetricCard>
  );
}
